import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Leaf, Compass, HandHeart, Boxes } from "lucide-react";
import StrategicAssociations from "@/components/StrategicAssociations";
import EPikChart from "@/components/EPikChart";

const HERO = "/photos-r3b/main-bg-facility-cleaned.jpg";
const FARMER = "/photos-r3b/multi-commodity-flatlay.jpg";

export default function About() {
  const { t } = useTranslation();

  const mvv = [
    { icon: Compass, k: "mission" },
    { icon: Leaf, k: "vision" },
    { icon: HandHeart, k: "values" },
  ];
  const chips = [
    t("about.chip_fruits"), t("about.chip_veg"), t("about.chip_grains"),
    t("about.chip_millets"), t("about.chip_pulses"), t("about.chip_spices"),
  ];

  return (
    <div data-testid="page-about">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Aerial farmland" className="w-full h-full object-cover" />
          <div className="absolute inset-0 tr-hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-40 text-white">
          <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">{t("about.hero_eyebrow")}</div>
          <div data-testid="about-hero-brand" className="mt-6 inline-flex items-center gap-4 rounded-2xl bg-white/90 backdrop-blur-md p-4 pr-6 shadow-2xl border border-white/40">
            <img
              src="/branding/logo.png"
              alt="TrueRoots Agro Foods LLP official logo"
              className="w-28 h-28 md:w-32 md:h-32 object-contain drop-shadow-md"
              loading="eager"
            />
            <div className="hidden sm:block">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--tr-zest)]">TrueRoots Agro Foods LLP</div>
              <div className="mt-1 font-display font-extrabold text-lg md:text-xl text-[var(--tr-deep-green)] leading-tight">Soil is the Source,<br />Purity is the Promise.</div>
            </div>
          </div>
          <h1 className="tr-rise mt-8 font-display font-extrabold text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight max-w-4xl">
            {t("about.hero_title_a")} <span className="text-[var(--tr-citrus-yellow)]">{t("about.hero_title_b")}</span> {t("about.hero_title_c")}
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="tr-eyebrow">{t("about.story_eyebrow")}</div>
          <h2 className="tr-section-title text-4xl lg:text-5xl mt-4">{t("about.story_title_a")}<br />{t("about.story_title_b")}</h2>
        </div>
        <div className="lg:col-span-7 space-y-6 text-[var(--tr-muted)] text-lg leading-relaxed">
          <p>{t("about.story_p1")}</p>
          <p>
            {t("about.story_p2_a")} <b className="text-[var(--tr-deep-green)]">{t("about.story_p2_b")}</b> {t("about.story_p2_c")}
          </p>
        </div>
      </section>

      <section className="bg-white border-y border-[rgba(6,78,59,0.08)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-3 gap-6">
          {mvv.map((c) => (
            <div key={c.k} className="tr-card p-8" data-testid={`about-card-${c.k}`}>
              <c.icon className="w-6 h-6 text-[var(--tr-zest)]" />
              <div className="mt-4 font-display font-extrabold text-2xl text-[var(--tr-deep-green)]">{t(`about.mv_${c.k}_t`)}</div>
              <p className="mt-3 text-[var(--tr-muted)] leading-relaxed">{t(`about.mv_${c.k}_d`)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="rounded-3xl overflow-hidden aspect-[4/5] border border-[rgba(6,78,59,0.1)]">
            <img src={FARMER} alt="Farmer" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="tr-eyebrow">{t("about.multi_eyebrow")}</div>
          <h2 className="tr-section-title text-4xl lg:text-5xl mt-4">{t("about.multi_title_a")}<br />{t("about.multi_title_b")}</h2>
          <p className="mt-6 text-[var(--tr-muted)] text-lg leading-relaxed">{t("about.multi_body")}</p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {chips.map((c) => (
              <div key={c} className="flex items-center gap-2 px-4 py-3 rounded-full bg-[var(--tr-sage)]/60 text-[var(--tr-deep-green)] font-semibold text-sm">
                <Boxes className="w-4 h-4" /> {c}
              </div>
            ))}
          </div>
          <Link to="/products/jalna-sweet-lime-mosambi" data-testid="about-view-mosambi" className="tr-pill-btn tr-btn-primary mt-10">
            {t("about.multi_cta")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* HIGH About Item 2 — e-Pik Pahani data as charts with narrative context */}
      <section className="bg-[var(--tr-sage)]/40 border-y border-[rgba(6,78,59,0.08)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="about-epik-block">
          <div className="max-w-3xl">
            <div className="tr-eyebrow">{t("about.epik_eyebrow")}</div>
            <h2 className="mt-4 tr-section-title text-3xl md:text-4xl">{t("about.epik_title")}</h2>
            <p className="mt-4 text-[var(--tr-muted)] leading-relaxed">{t("about.epik_body_1")}</p>
            <p className="mt-3 text-[var(--tr-muted)] leading-relaxed">{t("about.epik_body_2")}</p>
          </div>
          <div className="mt-10">
            <EPikChart variant="light" />
          </div>
        </div>
      </section>

      {/* HIGH #9 — Strategic Association panel for About */}
      <StrategicAssociations variant="dark" />
    </div>
  );
}
